import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'
import { Badge } from './ui/Badge'
import NotificationPanel from './NotificationPanel'

interface NotificationBellProps {
  className?: string
}

interface Notification {
  id: string
  type: string
  title: string
  body: string
  read: number
  created_at: string
  metadata?: string
  organization_id?: string
}

export default function NotificationBell({ className = '' }: NotificationBellProps) {
  const { user } = useAuthStore()
  const [unreadCount, setUnreadCount] = useState(0)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const bellRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return
    
    try {
      const response = await apiFetch(`${API_BASE}/notifications/unread-count`)
      if (response.ok) {
        const data = await response.json()
        setUnreadCount(data.count || 0)
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }, [user])

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const response = await apiFetch(`${API_BASE}/notifications?limit=20`)
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications || [])
        setUnreadCount(data.unread_count || 0)
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const response = await apiFetch(
        `${API_BASE}/notifications/${notificationId}/read`,
        { method: 'POST' }
      )
      
      if (response.ok) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, read: 1 } : n
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await apiFetch(
        `${API_BASE}/notifications/read-all`,
        { method: 'POST' }
      )
      
      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, read: 1 }))
        )
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  // Toggle panel
  const togglePanel = () => {
    const newOpen = !isPanelOpen
    setIsPanelOpen(newOpen)
    if (newOpen) {
      fetchNotifications()
    }
  }

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setIsPanelOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Poll for unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount()
    
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  // Refresh when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      fetchNotifications()
    }
  }, [isPanelOpen, fetchNotifications])

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={togglePanel}
        className={`
          relative p-2 rounded-lg transition-colors
          hover:bg-slate-100 dark:hover:bg-slate-700
          focus:outline-none focus:ring-2 focus:ring-blue-500
          ${className}
        `}
        aria-label={`Ilmoitukset${unreadCount > 0 ? `, ${unreadCount} lukematonta` : ''}`}
        aria-expanded={isPanelOpen}
        aria-haspopup="true"
      >
        <svg
          className="w-6 h-6 text-slate-600 dark:text-slate-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        
        {unreadCount > 0 && (
          <Badge
            variant="error"
            size="sm"
            className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 flex items-center justify-center"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {isPanelOpen && (
        <div ref={panelRef}>
          <NotificationPanel
            notifications={notifications}
            loading={loading}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onClose={() => setIsPanelOpen(false)}
          />
        </div>
      )}
    </div>
  )
}
